"use client"
// PC 首页「能力矩阵」区：TAB 弹簧 pill（与 gamelist2/solutions 同款交互）+ 能力点卡片
// 文案经 dict 注入（默认中文），TAB 内容入 lib/i18n/dicts/home.ts 的 section41 切片
import React, { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { cn } from '@/lib/utils'
import { homeDict, type HomeSection41Dict } from '@/lib/i18n/dicts/home'

export interface Section41Props {
    dict?: HomeSection41Dict
}

export const Section41 = ({ dict = homeDict.zh.section41 }: Section41Props) => {
    const [active, setActive] = useState(0)
    const current = dict.tabs[active] ?? dict.tabs[0]

    return (
        <section className="bg-white py-16">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <h2 className="text-base/7 font-semibold text-indigo-600">{dict.kicker}</h2>
                    <p className="mx-auto mt-2 max-w-2xl text-3xl font-semibold tracking-tight text-balance text-gray-950 sm:text-4xl">
                        {dict.heading}
                    </p>
                    <p className="mx-auto mt-4 max-w-2xl text-sm md:text-base text-gray-500">
                        {dict.sub}
                    </p>
                </motion.div>

                {/* TAB 弹簧 pill（layoutId 共享位移动画，与 gamelist2/solutions 一致） */}
                <div className="mt-10 flex flex-wrap justify-center gap-2">
                    {dict.tabs.map((tab, i) => (
                        <button
                            key={tab.tab}
                            onClick={() => setActive(i)}
                            className={cn(
                                'relative rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300',
                                active === i ? 'text-white' : 'text-gray-500 hover:text-gray-900'
                            )}
                        >
                            {active === i && (
                                <motion.span
                                    layoutId="home-capability-pill"
                                    className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 shadow-lg shadow-indigo-600/30"
                                    transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                                />
                            )}
                            <span className="relative z-10">{tab.tab}</span>
                        </button>
                    ))}
                </div>

                {/* 能力点卡片：TAB 切换时整体弹簧过渡 */}
                <div className="mt-10 min-h-[220px]">
                    <AnimatePresence mode="wait">
                        {current && (
                            <motion.div
                                key={active}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12, transition: { duration: 0.15 } }}
                                transition={{ type: 'spring', stiffness: 220, damping: 26 }}
                                className="mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row"
                            >
                                {current.points.map((point, i) => (
                                    <div
                                        key={point.title}
                                        className="group flex-1 rounded-2xl bg-slate-50 p-6 ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-1.5 hover:bg-white hover:shadow-xl hover:shadow-indigo-500/10 hover:ring-indigo-300/60"
                                    >
                                        <span className="text-2xl font-black bg-gradient-to-br from-indigo-600 to-fuchsia-600 bg-clip-text text-transparent">
                                            {String(i + 1).padStart(2, '0')}
                                        </span>
                                        <h3 className="mt-3 text-base font-bold text-gray-900 transition-colors duration-300 group-hover:text-indigo-700">
                                            {point.title}
                                        </h3>
                                        <p className="mt-2 text-xs leading-5 text-gray-500">{point.desc}</p>
                                    </div>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    )
}
